import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getReferralCode, clearReferralCache } from '../utils/shareWithReferral';
import {
  readToken,
  writeToken,
  clearToken,
  readUser,
  writeUser,
  clearUser,
} from '../utils/tokenStorage';

const AuthContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  // Tim's P0 brief Feb 17: brug multi-tier storage (localStorage +
  // cookie + sessionStorage) så token overlever iOS Safari ITP og
  // brugeren ALDRIG bliver logget ud uventet.
  // Initial-value via readToken() henter fra prioritetsrækkefølgen og
  // heal'er localStorage hvis token kun fandtes i cookie.
  const [user, setUser] = useState(() => readUser());
  const [token, setToken] = useState(() => readToken());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      // Persist user-object til localStorage så vi har en optimistic
      // initial-state ved næste boot — det undgår login-flicker når
      // brugeren genåbner appen mens vi venter på /auth/me-svar.
      writeUser(response.data);
      // Preload referral code for share functions
      getReferralCode();
      // Claim daily login points (silent, fire-and-forget)
      axios.post(`${API_URL}/points/daily-login`).catch(() => {});
      // Silent token refresh — hvis nuværende token er < 30 dage fra
      // udløb, hent en frisk 365-dages token i baggrunden. Sammen med
      // rolling-token-middleware (som rotater på hver authenticated
      // request når < 60 dage tilbage) sikrer det at brugeren ALDRIG
      // oplever et udløb i praksis.
      try {
        const t = readToken();
        if (t) {
          const parts = t.split('.');
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const exp = Number(payload?.exp);
            if (Number.isFinite(exp)) {
              const nowSec = Math.floor(Date.now() / 1000);
              const remaining = exp - nowSec;
              // Refresh aggressivt: < 30 dage tilbage → få en ny.
              if (remaining < 30 * 24 * 3600) {
                axios.post(`${API_URL}/auth/refresh`).then((r) => {
                  if (r?.data?.token) {
                    writeToken(r.data.token);
                    axios.defaults.headers.common['Authorization'] = `Bearer ${r.data.token}`;
                  }
                }).catch(() => { /* not critical */ });
              }
            }
          }
        }
      } catch { /* token parse fail → no big deal */ }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Tim's P0 brief Feb 17: KUN log ud ved EKSPLICIT auth-fejl
      // (401/403). Netværksfejl, 5xx, timeout etc. må IKKE føre til
      // logout — brugeren kan have mistet internet kortvarigt og skal
      // bare reconnecte uden at miste session.
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        logout();
      }
      // On network error, keep the token - user might just have bad connectivity
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, stayLoggedIn = true) => {
    // Trim whitespace BEFORE sending — mobile keyboards (especially
    // Samsung & iOS autocorrect) routinely inject trailing spaces into
    // email AND password fields. The user has no way to spot this
    // visually and ends up convinced the password is wrong. Trim first
    // so the network request only sees clean credentials.
    const cleanEmail = (email || '').trim().toLowerCase();
    const cleanPassword = (password || '').trim();
    // Default `stay_logged_in: true` så brugere får 30-dages tokens i
    // stedet for 24-timers tokens. På mobile PWA er det meget bedre UX —
    // brugeren skal ikke logge ind hver dag, og baggrunds-ride-polls
    // (rides/update hver 10s) bliver ikke pludselig kicked ud af 401.
    const response = await axios.post(`${API_URL}/auth/login`, { email: cleanEmail, password: cleanPassword, stay_logged_in: stayLoggedIn });
    const { token: newToken, user: userData } = response.data;
    // Multi-tier persistence — overlever iOS ITP localStorage purge.
    writeToken(newToken);
    writeUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, userType = 'user', nickname = '', clubName = '', clubType = 'free', companyFields = {}, clubFields = {}, referralCode = null) => {
    const payload = {
      email,
      password,
      name,
      nickname: userType === 'user' ? nickname : null,
      club_name: userType === 'club' ? clubName : null,
      user_type: userType,
      subscription_type: userType === 'club' ? clubType : null
    };

    // Add referral code if provided
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    
    // Add company-specific fields
    if (userType === 'company') {
      payload.company_name = companyFields.company_name || null;
      payload.phone = companyFields.phone || null;
      payload.website = companyFields.website || null;
      payload.offer_description = companyFields.offer_description || null;
    }
    
    // Add club-specific fields
    if (userType === 'club') {
      payload.no_clubhouse = clubFields.no_clubhouse || false;
      if (clubFields.opening_hours) {
        payload.opening_hours = clubFields.opening_hours;
      }
      payload.city = clubFields.city || '';
      payload.address = clubFields.address || '';
      payload.country = clubFields.country || '';
      if (clubFields.postal_code) payload.postal_code = clubFields.postal_code;
      if (clubFields.phone) payload.phone = clubFields.phone;
      if (clubFields.description) payload.description = clubFields.description;
      if (clubFields.facilities) payload.facilities = clubFields.facilities;
      if (clubFields.alternative_opening_hours) payload.alternative_opening_hours = clubFields.alternative_opening_hours;
      if (clubFields.special_opening_dates) payload.special_opening_dates = clubFields.special_opening_dates;
    }

    // For user type, pass country from companyFields (used as a carrier for country)
    if (userType === 'user' && companyFields.country) {
      payload.country = companyFields.country;
    }
    
    const response = await axios.post(`${API_URL}/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    writeToken(newToken);
    writeUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearToken();
    clearUser();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    clearReferralCache();
  };

  // Set auth state directly (used for password reset auto-login)
  const setAuth = (newToken, userData) => {
    writeToken(newToken);
    if (userData) writeUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
